/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AfterViewInit, Component } from '@angular/core';
import { DropdownModel } from '../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LogService, ModalService } from '../../../../../core/services';
import { nameValidation } from '../../../../../core/directives';

@Component({
    selector: 'app-lb-modals-real-server',
    template: `
        <app-modal-wrapper
            modalTitle="save_file"
            modalHeight="315"
            modalWidth="500"
            modalId="save">
            <app-modal-body>
                <form [formGroup]="saveForm">
                    <div class="form-group">
                        <label>{{ 'file_name' | myLbTranslate }}</label>
                        <div class="form-content">
                            <input
                                formControlName="fileName"
                                id="fileName"
                                name="fileName"
                                title="{{ 'file_name' | myLbTranslate }}" type="text">
                            <app-validation-messages
                                [control]="saveForm.controls['fileName']"></app-validation-messages>
                        </div>
                    </div>
                    <div class="form-group">
                        <label for="save-filetype">{{ 'file_type' | myLbTranslate }}</label>
                        <div class="form-content">
                            <app-dropdown
                                dropDownId="save-filetype"
                                formControlName="fileType"
                                dropdownWidth="100"
                                [formGroup]="saveForm"
                                [content]="dropdown.fileTypes">
                            </app-dropdown>
                        </div>
                    </div>
                </form>
            </app-modal-body>
            <app-modal-footer>
                <button class="outline" (click)="modalService.close('save')">{{ 'cancel' | myLbTranslate }}</button>
                <button class="primary" (click)="save()" [disabled]="!saveForm.valid">
                    {{ 'save' | myLbTranslate }}
                </button>
            </app-modal-footer>
        </app-modal-wrapper>
    `,
})

export class SaveModalComponent implements AfterViewInit {
    /**
     * Information to be saved.
     */
    public contentToSave: any;

    /**
     * If the information being saved has a title included, pass in the reference to the title and it will be given
     * the same name as the filename set in the form.
     */
    public fileNameReference: any;

    /**
     * Sets a default file type in the dropdown.
     */
    public preselectFileType: string;

    /**
     * Disables the file type dropdown, only applied if 'preselectFileType' is set.
     */
    public disabledFileType: boolean = false;

    /**
     * Callback function to call on completion.
     */
    public callBack: (...args: any[]) => void;

    /**
     * Frontend Form
     */
    public saveForm: FormGroup = new FormGroup({});

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown: any = {
        fileTypes: [],
    };

    constructor(
        private formBuilder: FormBuilder,
        private logService: LogService,
        public modalService: ModalService
    ) {
        this.setUpForm();
        this.setUpDropdownContent();
    }

    /**
     * Setup the view.
     */
    public ngAfterViewInit(): void {
        this.initForm();

        // Open our modal
        this.modalService.openModalInParent('save');
    }

    /**
     * Cancel button, submits observable action
     */
    public cancel(): void {
        this.modalService.close('save');
    }

    /**
     * Save the content to a file.
     */
    public save(): void {
        const fileName = this.saveForm.controls['fileName'].value;
        const fileType = this.saveForm.controls['fileType'].value;

        // Change content name if set.
        if (this.fileNameReference) {
            this.contentToSave[this.fileNameReference] = fileName;
        }

        // Convert content based on filetype selected.
        const formattedContent = this.convertContent(fileType, this.contentToSave);

        // Create the file itself.
        const file = new Blob([formattedContent], { type: fileType });

        if (file && file.type.length) {
            const a = document.createElement('a');
            a.href = URL.createObjectURL(file);
            a.download = fileName;
            a.click();
            this.modalService.closeAll();
            this.callBack();
        } else {
            this.logService.error('SaveComponent: save()', 'Error Creating File');
        }
    }

    /**
     * Set up the form for the page
     * @private
     */
    private setUpForm(): void {
        this.saveForm = this.formBuilder.group({
            fileName: [null, [nameValidation, Validators.required]],
            fileType: [null, Validators.required]
        });
    }

    /**
     * Patch the form values if set.
     * @private
     */
    private initForm(): void {
        if (this.preselectFileType) {
            const formValues = {
                fileType: this.preselectFileType
            };
            // Patch the value
            this.saveForm.patchValue(formValues);

            // Disable form element
            if (this.disabledFileType) {
                this.saveForm.controls['fileType'].disable();
            }
        }
    }

    /**
     * Setup dropdown arrays object.
     */
    private setUpDropdownContent(): void {
        this.dropdown.fileTypes = [
            <DropdownModel>{ key: 'application/json', value: 'json' },
            <DropdownModel>{ key: 'text/plain', value: 'txt' },
        ];
    }

    /**
     * Convert content to string and format
     * @private
     */
    private convertContent(fileType: string, data: any): string {
        switch (fileType) {
            case 'application/json':
                return JSON.stringify(data, null, 4);
            case 'text/plain':
                return JSON.stringify(data);
        }
    }
}
